import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { PATH_HOME } from '@jwp/ott-common/src/paths';
import { channelSubscribersURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { normalizeChannelHandle } from '@jwp/ott-common/src/utils/channelHandle';
import {
  GET_MY_CHANNEL,
  type GetMyChannelData,
  GET_CHANNEL_VIDEOS,
  GetChannelVideosData,
  GetChannelVideosVariables,
  ChannelVideo,
  GET_CHANNEL_HOME,
  GetChannelHomeData,
  GetChannelHomeVariables,
  ChannelHomeMedia,
} from '@jwp/ott-common/src/graphql/query/channel.queries';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { PERMISSIONS, hasPermission } from '@jwp/ott-common/src/constants';
import { resolveMediaUrl } from '@jwp/ott-common/src/utils/mediaUrl';
import CreateChannelBG from '@jwp/ott-theme/assets/icons/Create_channel_BG.svg';
import ArrowLeft from '@jwp/ott-theme/assets/icons/arrow_left.svg?react';
import MoreIcon from '@jwp/ott-theme/assets/icons/more.svg?react';
import SearchIcon from '@jwp/ott-theme/assets/icons/search.svg?react';
import PlusIcon from '@jwp/ott-theme/assets/icons/plusIcon.svg?react';
import MoviesIcon from '@jwp/ott-theme/assets/icons/Movies.svg?react';
import WebSeriesIcon from '@jwp/ott-theme/assets/icons/Web series.svg?react';
import IllustrationIcon from '@jwp/ott-theme/assets/icons/illustration.svg?react';
import ShareIcon from '@jwp/ott-theme/assets/icons/Share_icon.svg?react';
import DeleteIcon from '@jwp/ott-theme/assets/icons/delete.svg?react';
import DeleteChannelIcon from '@jwp/ott-theme/assets/icons/Delete_icon.svg?react';
import WarningIcon from '@jwp/ott-theme/assets/icons/warning.svg?react';
import FlagIcon from '@jwp/ott-theme/assets/icons/Flag.svg?react';
import AllSubscribeIcon from '@jwp/ott-theme/assets/icons/All_subscribe.svg?react';
import UnfollowIcon from '@jwp/ott-theme/assets/icons/UnfollowIcon.svg?react';
import FollowIcon from '@jwp/ott-theme/assets/icons/FollowIcon.svg?react';
import EditChannelIcon from '@jwp/ott-theme/assets/icons/Edit_channel.svg?react';
import InfoDelIcon from '@jwp/ott-theme/assets/icons/Info_del.svg?react';
import ChevronLeft from '@jwp/ott-theme/assets/icons/chevron_left.svg?react';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';
import { createURLFromLocation, modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import useBreakpoint, { Breakpoint, type Breakpoints } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import Button from '../../components/Button/Button';
import Icon from '../../components/Icon/Icon';
import IconButton from '../../components/IconButton/IconButton';
import Dialog from '../../components/Dialog/Dialog';
import ChannelAboutModal from '../../components/ChannelAboutModal/ChannelAboutModal';
import Spinner from '../../components/Spinner/Spinner';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import useDebounce from '@jwp/ott-hooks-react/src/useDebounce';
import CheckIcon from '@jwp/ott-theme/assets/icons/check.svg?react';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import env from '@jwp/ott-common/src/env';
import { PATH_VIDEO_PREVIEW, PATH_MY_VIDEO_DETAILS } from '@jwp/ott-common/src/paths';
import Card from '../../components/Card/Card';
import { TileSlider, type RenderControl } from '../../components/TileDock/TileDock';
import Popover from '../../components/Popover/Popover';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import Alert from '../../components/Alert/Alert';
import InfoIcon from '@jwp/ott-theme/assets/icons/info.svg?react';
import EditIcon from '@jwp/ott-theme/assets/icons/edit.svg?react';
import { useMutation } from '@apollo/client/react';
import { DELETE_MEDIA_BY_MEDIA_ID, DeleteMediaByMediaIdData } from '@jwp/ott-common/src/graphql/mutation/reels.mutation';
import { REMOVE_CHANNEL, type RemoveChannelData, type RemoveChannelVariables } from '@jwp/ott-common/src/graphql/mutation/channel.mutations';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';
import styles from './ChannelPage.module.scss';
import ChannelSearchBar from '../../components/ChannelSearch/ChannelSearch';
import ChannelSearchResults from '../../components/ChannelSearch/ChannelSearchResults';
import ReportChannelModal from '../../components/ReportsModal/ReportChannelModal';
import { useFollowChannel } from '../../hooks/useFollowChannel';

const TAB_KEYS = ['home', 'reels', 'videos', 'search'] as const;
type ChannelTabKey = (typeof TAB_KEYS)[number];

const CHANNEL_REELS_PAGE_SIZE = 10;

const HOME_REEL_BREAKPOINTS: Breakpoints = {
  [Breakpoint.xs]: 2.5,
  [Breakpoint.sm]: 3.5,
  [Breakpoint.md]: 5,
  [Breakpoint.lg]: 6,
  [Breakpoint.xl]: 7,
};

const HOME_VIDEO_BREAKPOINTS: Breakpoints = {
  [Breakpoint.xs]: 1.5,
  [Breakpoint.sm]: 2,
  [Breakpoint.md]: 3,
  [Breakpoint.lg]: 4,
  [Breakpoint.xl]: 5,
};

const isReelMedia = (item: { maType?: string | null }) => {
  const type = (item.maType || '').toLowerCase();
  return type === 'reels' || type === 'reel';
};

const toChannelVideo = (item: ChannelHomeMedia | ChannelVideo): ChannelVideo =>
  ({
    ...item,
    ageRatingInfo: 'ageRatingInfo' in item ? item.ageRatingInfo ?? null : null,
    likeCount: 'likeCount' in item ? item.likeCount ?? null : null,
    isLikedByUser: 'isLikedByUser' in item ? item.isLikedByUser ?? null : null,
    isFollowing: 'isFollowing' in item ? item.isFollowing ?? null : null,
    genres: 'genres' in item ? item.genres ?? null : null,
  }) as ChannelVideo;

const toPlaylistItem = (item: ChannelHomeMedia, isReel: boolean): PlaylistItem =>
  ({
    mediaid: item.maId,
    title: item.maTitle,
    duration: item.maDuration ? Number(item.maDuration) / 1000 : 0,
    cardImage: `${env.APP_BASE_URL || ''}${item.maS3ThumbnailKeyUrl}`,
    images: [],
    link: '',
    pubdate: 0,
    sources: [],
    feedid: '',
    description: item.maDescription,
    isReported: item.isReported || false,
  }) as unknown as PlaylistItem;

// /static avoids DEV media CDN which serves /images/*
const DEFAULT_CHANNEL_AVATAR = '/static/default-avatar.png';
const CHANNEL_HOME_LIMIT = 10;

const ChannelPage = () => {
  const { t } = useTranslation(['user', 'video', 'common', 'upload']);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const u = queryParams.get('u');
  const [lastU, setLastU] = useState<string | null>(u);
  const { handle: routeHandle = '', chId } = useParams();
  const breakpoint = useBreakpoint();
  const user = useAccountStore((state) => state.user);
  const canUpdateChannel = hasPermission(user, PERMISSIONS.UPDATE_CHANNEL);
  const canDeleteChannel = hasPermission(user, PERMISSIONS.DELETE_CHANNEL);
  const canReportChannel = hasPermission(user, PERMISSIONS.CREATE_CHANNEL_REPORT);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ChannelTabKey>('home');
  const [fileType, setFileType] = useState<'video' | 'reel' | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ x: number; y: number; isBottom?: boolean } | null>(null);
  const [selectedItem, setSelectedItem] = useState<ChannelVideo | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [deleteChannelOpen, setDeleteChannelOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const isChannelAvailable = useAccountStore((state) => state.isChannelAvailable);
  const myAccountId = useAccountStore((state) => state.user?.id);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const uploadMenuRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);


  const {
    setFile,
    setThumbnail,
    setDuration,
    setPreviewUrl,
    setValues,
    setReturnPath,
    setMediaId,
    setInitiated,
    reset: resetStore
  } = useUploadStore();

  const { data, loading, error, refetch } = useQuery<GetMyChannelData>(GET_MY_CHANNEL, {
    client: getAuthApolloClient(),
    fetchPolicy: 'network-only',
    variables: chId ? { chId: chId } : {}
  });

  const channel = useMemo(() => {
    const channels = data?.getMyChannel?.channels || [];
    if (!channels.length) {
      return null;
    }

    const normalizedRouteHandle = normalizeChannelHandle(routeHandle);
    const matched = channels.find(
      (item) => normalizeChannelHandle(item.chHandle) === normalizedRouteHandle,
    );

    return matched || channels[0];
  }, [data, routeHandle]);

  const normalUser = !!chId && myAccountId !== channel?.userInfo?.uId;

  const shortDescription = channel?.chDescription
    ? channel.chDescription.length > 200
      ? `${channel.chDescription.slice(0, 200)}...`
      : channel.chDescription
    : '';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (actionMenuRef.current && !actionMenuRef.current.contains(target)) {
        setActionMenuOpen(false);
      }
      if (uploadMenuRef.current && !uploadMenuRef.current.contains(target)) {
        setUploadMenuOpen(false);
      }
    };

    if (actionMenuOpen || uploadMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionMenuOpen, uploadMenuOpen]);

  useEffect(() => {
    const handleResize = () => setMenuAnchor(null);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, []);

  const [reportedOpen, setReportedOpen] = useState(false);
  const reportedStatus = error?.message == 'This channel has been reported.' || channel?.isReported;
  useEffect(() => {
    if (!reportedStatus) return;

    if (normalUser) {
      setReportedOpen(true);
    } else {
      showToast(
        t('channel.reported_toast.message', {
          defaultValue: 'This channel has been reported.'
        }),
        'error'
      );
    }
  }, [channel?.isReported, normalUser, error]);


  const handleReportedDialogClose = () => {
    setReportedOpen(true);
  };

  const handleReportedOkClick = () => {
    setReportedOpen(false);
    navigate(PATH_HOME, { replace: true });
  };


  const onFileSelect = (selectedFile: File | null) => {
    if (selectedFile) {
      if (!selectedFile.type.startsWith('video/')) {
        setFile(null);
        setThumbnail(null, null);
        return;
      }
      if (selectedFile.size > 5 * 1024 * 1024 * 1024) {
        setFile(null);
        setThumbnail(null, null);
        return;
      }
    }

    setThumbnail(null, null);
    setFile(selectedFile);

    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      const vid = document.createElement('video');
      vid.preload = 'metadata';
      vid.onloadedmetadata = () => {
        const d = Number.isFinite(vid.duration) ? vid.duration : null;
        if (d !== null) {
          setDuration(Math.floor(d * 1000));
        }
        URL.revokeObjectURL(url);
      };
      setDuration(null);
      vid.src = url;
    } else {
      setDuration(null);
    }

    const { setInitiated } = useUploadStore.getState();
    setInitiated(true);

    setTimeout(() => {
      navigate(createURLFromLocation(location, { u: fileType === 'reel' ? 'upload-reel' : 'upload-video' }));
    }, 1000);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    onFileSelect(selectedFile);
  };

  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };


  const maTypeForTab = activeTab === 'reels' ? 'reels' : activeTab === 'videos' ? 'videos' : null;

  const getChannelVideosVariables = (maType: 'reels' | 'videos', offset = 0): GetChannelVideosVariables => ({
    input: {
      first: 12,
      offset,
      maType,
      maUploadStatus: 'uploaded',
      chId: channel ? [channel.chId] : [],
    },
  });

  const channelHomeQuery = useQuery<GetChannelHomeData, GetChannelHomeVariables>(GET_CHANNEL_HOME, {
    client: getAuthApolloClient(),
    variables: {
      input: {
        limit: CHANNEL_HOME_LIMIT,
        offset: 0,
        maUploadStatus: 'uploaded',
        chId: channel?.chId || '',
      },
    },
    skip: !channel?.chId,
    fetchPolicy: 'network-only',
  });

  const channelVideosQuery = useQuery<GetChannelVideosData, GetChannelVideosVariables>(GET_CHANNEL_VIDEOS, {
    client: getAuthApolloClient(),
    variables: getChannelVideosVariables('videos'),
    skip: !channel?.chId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const channelReelsQuery = useQuery<GetChannelVideosData, GetChannelVideosVariables>(GET_CHANNEL_VIDEOS, {
    client: getAuthApolloClient(),
    variables: getChannelVideosVariables('reels'),
    skip: !channel?.chId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const [deleteMediaApproval, { loading: deleteLoading }] = useMutation<DeleteMediaByMediaIdData, { maId: string }>(DELETE_MEDIA_BY_MEDIA_ID, {
    onCompleted: () => {
      setConfirmationOpen(false);
      setSuccessOpen(true);
      refetch()
      channelVideosQuery.refetch();
      channelReelsQuery.refetch();
      channelHomeQuery.refetch();
    },
    onError: (error) => {
      console.error('Delete failed:', error);
      setErrorMessage('Failed to delete video. Please try again.');
      setErrorOpen(true);
    },
  });

  const [removeChannel, { loading: removeChannelLoading }] = useMutation<RemoveChannelData, RemoveChannelVariables>(REMOVE_CHANNEL, {
    client: getAuthApolloClient(),
  });

  const { isFollowing, isBusy: isFollowBusy, toggleFollow: handleFollowToggle } = useFollowChannel(channel?.chId);

  const finishChannelRemoved = useCallback(() => {
    setDeleteChannelOpen(false);
    useAccountStore.setState({
      isChannelAvailable: false,
      myChannelHandle: null,
    });
    navigate(PATH_HOME, { replace: true });
  }, [navigate]);

  const verifyChannelRemoved = useCallback(
    async (chId: string) => {
      try {
        const verify = await getAuthApolloClient().query<GetMyChannelData>({
          query: GET_MY_CHANNEL,
          fetchPolicy: 'network-only',
        });

        return !(verify.data?.getMyChannel?.channels || []).some((item) => item.chId === chId);
      } catch (verifyError) {
        console.error('Remove channel verify failed:', verifyError);
        return false;
      }
    },
    [],
  );

  const handleRemoveChannel = useCallback(async () => {
    if (!channel?.chId) {
      return;
    }

    const chId = channel.chId;

    try {
      const response = await removeChannel({
        variables: { chId },
        errorPolicy: 'ignore',
      });

      const result = response.data?.removeChannel;

      if (result !== false && result !== null && result !== undefined && !response.error) {
        finishChannelRemoved();
        return;
      }

      // Backend sometimes deletes successfully but returns null for a non-null field.
      // Confirm by checking whether the channel still exists before surfacing a failure.
      const removed = await verifyChannelRemoved(chId);
      if (removed) {
        finishChannelRemoved();
        return;
      }

      setErrorMessage(t('channel.delete.failed'));
      setErrorOpen(true);
    } catch (error) {
      const removed = await verifyChannelRemoved(chId);
      if (removed) {
        finishChannelRemoved();
        return;
      }

      console.error('Remove channel failed:', error);
      setErrorMessage(t('channel.delete.failed'));
      setErrorOpen(true);
    }
  }, [channel?.chId, finishChannelRemoved, removeChannel, t, verifyChannelRemoved]);

  const currentQuery = activeTab === 'reels' ? channelReelsQuery : channelVideosQuery;
  const channelVideos = currentQuery.data?.getMyVideos?.videos || [];
  const channelVideosTotal = currentQuery.data?.getMyVideos?.totalCount || 0;
  const videosTotal = channel?.videosCount ?? 0;
  const onLoading = currentQuery.loading;
  const homeReels = channelHomeQuery.data?.getChannelHome?.reels || [];
  const homeVideos = channelHomeQuery.data?.getChannelHome?.videos || [];
  const hasHomeContent = homeReels.length > 0 || homeVideos.length > 0;
  const selectedIsReel = selectedItem ? isReelMedia(selectedItem) : activeTab === 'reels';
  const homeReelTilesToShow = HOME_REEL_BREAKPOINTS[breakpoint];
  const homeVideoTilesToShow = HOME_VIDEO_BREAKPOINTS[breakpoint];
  const [searchActive, setSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => {
    return queryParams.get('search') || '';
  });
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(() => {
    return queryParams.get('search') || '';
  });


  const debouncedSetSearchQuery = useDebounce((query: string) => {
    setDebouncedSearchQuery(query);
  }, 350);

  const handleSearchQueryChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      setActiveTab('search');

      // Update URL instantly
      const newParams = new URLSearchParams(location.search);
      if (query) {
        newParams.set('search', query);
      } else {
        newParams.delete('search');
      }
      newParams.set('tab', 'search');

      window.history.replaceState(
        null,
        '',
        `${location.pathname}?${newParams.toString()}`
      );

      // Debounce the API call
      debouncedSetSearchQuery(query);
    },
    [debouncedSetSearchQuery, location.pathname, location.search]
  );

  const handleTabSwitch = (tab: ChannelTabKey) => {
    setActiveTab(tab)
    setMenuAnchor(null)
    setSearchActive(false)
    setDebouncedSearchQuery('')
    setSearchQuery('')

    // Clear search params from URL
    const newParams = new URLSearchParams(location.search);
    newParams.delete('search');
    newParams.set('tab', tab);
    window.history.replaceState(
      null,
      '',
      `${location.pathname}?${newParams.toString()}`
    );
  }

  const renderLeftControl: RenderControl = useCallback(
    ({ onClick, disabled }) => (
      <button type="button" style={{ display: disabled ? 'none' : 'block' }} className={styles.chevron} aria-label={t('common:slide_previous')} onClick={onClick}>
        <Icon icon={ChevronLeft} />
      </button>
    ),
    [t],
  );

  const renderRightControl: RenderControl = useCallback(
    ({ onClick, disabled }) => (
      <button type="button" style={{ display: disabled ? 'none' : 'block' }} className={styles.chevron} aria-label={t('common:slide_next')} onClick={onClick}>
        <Icon icon={ChevronRight} />
      </button>
    ),
    [t],
  );

  useEffect(() => {
    const tab = queryParams.get('tab');
    if (tab === 'home' || tab === 'reels' || tab === 'videos' || tab == 'search') {
      if (tab === 'search') {
        setSearchActive(true)
      }
      setActiveTab(tab);
    }
  }, [queryParams]);

  useEffect(() => {
    if ((lastU === 'upload-success' || lastU === 'edit-success') && !u) {
      refetch()
      channelVideosQuery.refetch();
      channelReelsQuery.refetch();
      if (channel?.chId) {
        channelHomeQuery.refetch();
      }
    }
    setLastU(u);
  }, [u, lastU, channelVideosQuery, channelReelsQuery, channelHomeQuery, channel?.chId]);

  const handleLoadMoreChannelVideos = () => {
    if (!maTypeForTab || channelVideos.length >= channelVideosTotal) return;

    currentQuery.fetchMore({
      variables: getChannelVideosVariables(maTypeForTab as 'reels' | 'videos', channelVideos.length),
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          getMyVideos: {
            ...fetchMoreResult.getMyVideos,
            videos: [...(prev.getMyVideos?.videos || []), ...(fetchMoreResult.getMyVideos?.videos || [])],
          },
        };
      },
    });
  };


  const handleShowDetails = () => {
    setMenuAnchor(null);
    if (selectedItem) {
      navigate(createURLFromLocation(location, { u: 'reel-details', id: selectedItem.maId }));
    }
  };

  const handleEditItem = () => {
    setMenuAnchor(null);
    if (!selectedItem) return;

    setMediaId(selectedItem.maId);
    setPreviewUrl(`${env.APP_BASE_URL || ''}${selectedItem.maS3MediaKeyUrl}`);
    setDuration(selectedItem.maDuration ? Number(selectedItem.maDuration) : null);

    const tagsString = Array.isArray(selectedItem.maTags)
      ? selectedItem.maTags.join(', ')
      : typeof selectedItem.maTags === 'string'
        ? selectedItem.maTags
        : '';

    setThumbnail(null, `${env.APP_BASE_URL || ''}${selectedItem.maS3ThumbnailKeyUrl}`);
    setValues({
      title: selectedItem.maTitle,
      description: selectedItem.maDescription || '',
      category: selectedItem.maCategory || '',
      tags: tagsString,
      fileName: selectedItem.maOriginalFileName || '',
      fileSize: String(selectedItem.maFileSize ?? ''),
    });

    setReturnPath(location.pathname + location.search);
    setInitiated(true);
    navigate(createURLFromLocation(location, { u: isReelMedia(selectedItem) ? 'upload-reel' : 'upload-video' }));
  };

  const handleDeleteItem = () => {
    setMenuAnchor(null);
    setConfirmationOpen(true);
  };


  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.emptyState}>
          <Spinner />
        </div>
      </div>
    );
  }

  if (error || !channel) {
    if (reportedStatus) {
      return (
        <div className={styles.page}>
          <Dialog
            open={reportedOpen}
            onClose={handleReportedDialogClose}
            role="alertdialog"
            shouldNotCloseButton={false}
          >
            <div className={styles.successDialog}>
              <div className={styles.warningIconContainer}>
                <Icon icon={WarningIcon} />
              </div>
              <h2>{t('channel.reported.title', { defaultValue: 'Channel Reported' })}</h2>
              <p>{t('channel.reported.description', { defaultValue: 'This channel has been reported and is under review.' })}</p>
              <Button
                label={t('common:okay', { defaultValue: 'Okay' })}
                variant="contained"
                color="primary"
                onClick={handleReportedOkClick}
              />
            </div>
          </Dialog >
        </div>
      )
    }
    return (
      <div className={styles.page}>
        <div className={styles.emptyState}>
          <div className={styles.emptyTitle}>{t('channel.not_found_title')}</div>
          <div className={styles.emptyDescription}>{t('channel.not_found_description')}</div>
          <Button label={t('channel.back_to_home')} variant="contained" color="primary" onClick={() => navigate(PATH_HOME)} />
        </div>
      </div>
    );
  }

  const displayHandle = normalizeChannelHandle(channel.chHandle);
  const bannerSrc = resolveMediaUrl(channel.chBannerUrl, CreateChannelBG);
  const avatarSrc = resolveMediaUrl(channel.chAvatarUrl, DEFAULT_CHANNEL_AVATAR);

  const handleUploadMenuToggle = (type: string) => {
    resetStore();
    setUploadMenuOpen(false);
    setFileType(type as 'video' | 'reel');
    triggerFileSelect();
  };

  const openMediaMenu = (e: React.MouseEvent, item: ChannelHomeMedia | ChannelVideo) => {
    e.preventDefault();
    e.stopPropagation();
    const mediaItem = toChannelVideo(item);
    if (menuAnchor && selectedItem?.maId === mediaItem.maId) {
      setMenuAnchor(null);
    } else {
      setSelectedItem(mediaItem);
      const rect = e.currentTarget.getBoundingClientRect();
      const popoverHeight = 250;
      const isBottom = rect.bottom + popoverHeight > window.innerHeight;
      setMenuAnchor({ x: rect.right, y: isBottom ? rect.top : rect.bottom, isBottom });
    }
  };

  const handleMediaClick = (e: React.MouseEvent, item: ChannelHomeMedia | ChannelVideo) => {
    e.preventDefault();
    const isReel = isReelMedia(item);
    if (isReel) {
      setPreviewUrl(`${env.APP_BASE_URL || ''}${item.maS3MediaKeyUrl}`);
      setValues({
        title: item.maTitle,
        description: item.maDescription || '',
        category: item.maCategory || '',
        tags: Array.isArray(item.maTags) ? item.maTags.join(', ') : typeof item.maTags === 'string' ? item.maTags : '',
      });
      setReturnPath(location.pathname + location.search);
      const currentOffset = Math.floor(channelVideos.indexOf(item as ChannelVideo) / CHANNEL_REELS_PAGE_SIZE) * CHANNEL_REELS_PAGE_SIZE;
      const otherChannelParam = chId
        ? `${PATH_VIDEO_PREVIEW}/${chId}/${item.maId}`
        : `${PATH_VIDEO_PREVIEW}/${item.maId}`;
      navigate(`${otherChannelParam}?returnPath=${encodeURIComponent(location.pathname + location.search)}&offset=${currentOffset}`);
    } else {
      navigate(`${PATH_MY_VIDEO_DETAILS}/${item.maId}?returnPath=${encodeURIComponent(location.pathname + location.search)}`);
    }
  };


  const renderHomeShelf = (items: ChannelHomeMedia[], isReel: boolean, title: string) => {
    const tilesToShow = isReel ? homeReelTilesToShow : homeVideoTilesToShow;
    return (
      <section className={styles.section}>
        <div className={styles.shelf}>
          <h2 className={styles.sectionTitle}>{title}</h2>
          <TileSlider<ChannelHomeMedia>
            className={styles.slider}
            items={items}
            tilesToShow={tilesToShow}
            cycleMode="stop"
            showControls
            spacing={14}
            renderLeftControl={renderLeftControl}
            renderRightControl={renderRightControl}
            renderPagination={null}
            isShowTopTenIconFlag={false}
            isOverlayTestVisible={true}
            shelfTitle={title}
            featured={false}
            activeScreen="channel_home"
            renderTile={({ item, isVisible, index }) => {
              const canEditOrDelete = hasPermission(user, PERMISSIONS.UPDATE_MEDIA) || hasPermission(user, PERMISSIONS.DELETE_MEDIA);
              const showMenu = isReel || canEditOrDelete;
              const canShowMenu = showMenu && !normalUser && !channel?.isReported && !item?.isReported
              const itemOffset = Math.floor(index / CHANNEL_REELS_PAGE_SIZE) * CHANNEL_REELS_PAGE_SIZE;
              const otherChannelParam = chId ? `${PATH_VIDEO_PREVIEW}/${chId}` : PATH_VIDEO_PREVIEW;
              return (
                <Card
                  item={toPlaylistItem(item, isReel)}
                  url={isReel ? `${otherChannelParam}/${item.maId}?returnPath=${encodeURIComponent(location.pathname)}&offset=${itemOffset}` : '#'}
                  posterAspect={isReel ? '9:16' : '16:9'}
                  status={item.maStatus as 'APPROVED' | 'PENDING' | 'REJECTED' | 'approved' | 'pending' | 'rejected' | 'scheduled' | 'live'}
                  isReels={isReel}
                  isOverlayTestVisible={true}
                  disabled={!isVisible}
                  onMenuClick={canShowMenu ? (e) => openMediaMenu(e, item) : undefined}
                  onClick={(e) => handleMediaClick(e, item)}
                />
              );
            }}
          />
        </div>
      </section>
    );
  };


  const renderSearch = () => {
    return (
      <ChannelSearchBar
        searchActive={searchActive}
        onSearchActiveChange={(active) => {
          setSearchActive(active);
          if (!active) {
            // Clear search when closing
            const newParams = new URLSearchParams(location.search);
            newParams.delete('search');
            newParams.set('tab', 'home');
            window.history.replaceState(
              null,
              '',
              `${location.pathname}?${newParams.toString()}`
            );
            setSearchQuery('');
            setDebouncedSearchQuery('');
            setActiveTab('home');
          } else {
            setActiveTab('search');
            setMenuAnchor(null);
          }
        }}
        onQueryChange={handleSearchQueryChange}
        searchQuery={searchQuery}
      />
    );
  };


  const renderEmptyState = (isReel: boolean = false) => (
    <div className={styles.emptyState}>
      <IllustrationIcon className={styles.emptyIcon} aria-hidden="true" />
      <div className={styles.emptyTitle}>{t('channel.empty.title')}</div>
      {!normalUser && (
        <div className={styles.emptyDescription}>
          {t('channel.empty.description')}
        </div>
      )}
    </div>
  );

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept='video/*'
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <div className={styles.page}>
        <div className={styles.banner} style={{ backgroundImage: `url(${bannerSrc})` }}>
          <IconButton aria-label="Back to home" onClick={() => navigate(PATH_HOME)} className={styles.backButton}>
            <Icon icon={ArrowLeft} />
          </IconButton>
        </div>

        <div className={styles.header}>
          <div className={styles.headerTop}>
            <img src={avatarSrc} alt="" className={styles.avatar} />

            <div className={styles.info}>
              <h1 className={styles.title}>{channel.chName}</h1>
              <div className={styles.meta}>
                <span className={styles.handle}>@{displayHandle}</span>
                {/* <span className={styles.metaDivider}>|</span>
                <span>0 Following</span>
                <span className={styles.metaDivider}>|</span>
                <span>0 Followers</span> */}
                <span className={styles.metaDivider}>|</span>
                <span>{videosTotal} Videos</span>
              </div>
            </div>

            <div className={styles.moreMenuWrap} ref={actionMenuRef}>
              <button type="button" className={styles.iconMenuButton} disabled={channel?.isReported} onClick={() => setActionMenuOpen((prev) => !prev)}>
                <Icon icon={MoreIcon} />
              </button>
              {actionMenuOpen && (
                <div className={styles.dropdown}>
                  <button type="button" className={styles.dropdownItem} onClick={() => {
                    setActionMenuOpen(false);
                    const shareUrl = `${window.location.origin}/m/channel/${channel.chId}`;
                    if (navigator.share) {
                      navigator.share({
                        title: channel.chName,
                        text: shortDescription,
                        url: shareUrl,
                      });
                    } else {
                      // Fallback: copy to clipboard
                      navigator.clipboard.writeText(shareUrl);
                      showToast('Channel link copied!', 'success');
                    }
                  }}
                  >
                    <Icon icon={ShareIcon} className={styles.shareMenuIcon} />
                    {normalUser ? t('channel.share_label') : t('channel.share_channel_label')}
                  </button>
                  {normalUser && canReportChannel &&
                    <>
                      <div className={styles.dropdownDivider} />
                      <button
                        type="button"
                        className={styles.dropdownItem}
                        onClick={() => {
                          setActionMenuOpen(false);
                          setReportModalOpen(true);
                        }}
                      >
                        <Icon icon={FlagIcon} className={styles.deleteMenuIcon} />
                        {t('channel.report_button')}
                      </button>
                    </>
                  }
                  {canDeleteChannel && !normalUser && (
                    <>
                      <div className={styles.dropdownDivider} />
                      <button
                        type="button"
                        className={styles.dropdownItemDanger}
                        onClick={() => {
                          setActionMenuOpen(false);
                          setDeleteChannelOpen(true);
                        }}
                      >
                        <Icon icon={DeleteChannelIcon} className={styles.deleteMenuIcon} />
                        {t('channel.delete.menu_label')}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <p className={styles.description}>
            {shortDescription}{' '}
            <button type="button" className={styles.moreLink} onClick={() => setDetailsOpen(true)}>
              More
            </button>
          </p>

          {normalUser ?
            <div className={styles.headerActions}>
              <Button
                label={t('channel.all_subscribers_label')}
                variant="transparent"
                startIcon={<Icon icon={AllSubscribeIcon} className={styles.subscribeActionIcon} />}
                className={styles.secondaryButton}
                onClick={() => navigate(channelSubscribersURL(channel.chId))}
                disabled={!!channel?.isReported}
              />
              <Button
                label={isFollowing ? t('channel.unfollow_label') : t('channel.follow_label')}
                variant={isFollowing ? 'transparent' : 'contained'}
                color={isFollowing ? 'default' : 'primary'}
                startIcon={
                  <Icon
                    icon={isFollowing ? UnfollowIcon : FollowIcon}
                    className={isFollowing ? styles.subscribeActionIcon : styles.followActionIcon}
                  />
                }
                className={classNames(styles.secondaryButton, { [styles.followButton]: !isFollowing })}
                onClick={handleFollowToggle}
                disabled={isFollowBusy || !!channel?.isReported}
              />
            </div> :
          !normalUser && <div className={styles.headerActions}>
            <Button
              label={t('channel.all_subscribers_label')}
              variant="transparent"
              startIcon={<Icon icon={AllSubscribeIcon} className={styles.subscribeActionIcon} />}
              className={styles.secondaryButton}
              onClick={() => navigate(channelSubscribersURL(channel.chId))}
              disabled={!!channel?.isReported}
            />
            {canUpdateChannel && (
              <Button
                label={t('edit_channel.title')}
                variant="transparent"
                startIcon={<Icon icon={EditChannelIcon} className={styles.editActionIcon} />}
                className={styles.secondaryButton}
                onClick={() => navigate(modalURLFromLocation(location, 'edit-channel'))}
                disabled={channel?.isReported}
              />
            )}
          </div>}         
        </div>

        <div className={styles.toolbar}>
          <div className={styles.tabs}>
            {TAB_KEYS.map((tab) => {
              return tab !== 'search' && (
                <button
                  key={tab}
                  type="button"
                  className={classNames(styles.tab, { [styles.activeTab]: activeTab === tab })}
                  onClick={() => {
                    handleTabSwitch(tab)
                  }}
                >
                  {t(`channel.tabs.${tab}`)}
                </button>
              )
            })}
          </div>

          <div className={styles.uploadArea} ref={uploadMenuRef}>
            {renderSearch()}
            {!normalUser && <button type="button" className={styles.uploadButton}
              disabled={channel?.isReported}
              onClick={() => setUploadMenuOpen((prev) => !prev)}>
              <PlusIcon className={styles.plusIcon} aria-hidden="true" />
              {t('channel.upload_content')}
            </button>}
            {uploadMenuOpen && (
              <div className={styles.uploadMenu}>
                <button
                  type="button"
                  className={styles.uploadMenuItem}
                  onClick={() => {
                    handleUploadMenuToggle('reel');
                  }}
                >
                  <MoviesIcon className={styles.uploadMenuIcon} aria-hidden="true" />
                  {t('channel.upload_reel')}
                </button>
                <div className={styles.dropdownDivider} />
                <button
                  type="button"
                  className={styles.uploadMenuItem}
                  onClick={() => {
                    handleUploadMenuToggle('video');
                  }}
                >
                  <WebSeriesIcon className={styles.uploadMenuIcon} aria-hidden="true" />
                  {t('channel.upload_video')}
                </button>
              </div>
            )}
          </div>
        </div>

        {activeTab === 'home' && (
          <>
            {channelHomeQuery.loading && !hasHomeContent ? (
              <div className={styles.emptyState}>
                <Spinner />
              </div>
            ) : !hasHomeContent ? (
              renderEmptyState()
            ) : (
              <>
                {homeReels.length > 0 && renderHomeShelf(homeReels, true, t('channel.sections.reels'))}
                {homeVideos.length > 0 && renderHomeShelf(homeVideos, false, t('channel.sections.videos'))}
              </>
            )}
          </>
        )}

        {(activeTab === 'reels' || activeTab === 'videos') && (
          <div className={channelVideos.length === 0 ? styles.container : styles.section}>
            {currentQuery.loading && channelVideos.length === 0 ? (
              <div className={styles.emptyState}>
                <Spinner />
              </div>
            ) : channelVideos.length === 0 ? (
              renderEmptyState()
            ) : (
              <div className={classNames(styles.grid, { [styles.reelsGrid]: activeTab === 'reels', [styles.videosGrid]: activeTab === 'videos' })}>
                {channelVideos.map((item, index) => {
                  const isReel = activeTab === 'reels';
                  const cardItem: PlaylistItem = {
                    mediaid: item.maId,
                    title: item.maTitle,
                    duration: item.maDuration ? Number(item.maDuration) / 1000 : 0,
                    cardImage: `${env.APP_BASE_URL || ''}${item.maS3ThumbnailKeyUrl}`,
                    images: [],
                    link: '',
                    pubdate: 0,
                    sources: [],
                    feedid: '',
                    description: item.maDescription,
                    isReported: item.isReported || false,
                  } as unknown as PlaylistItem;

                  const canEditOrDelete = hasPermission(user, PERMISSIONS.UPDATE_MEDIA) || hasPermission(user, PERMISSIONS.DELETE_MEDIA);
                  const showMenu = isReel || canEditOrDelete;
                  const canShowMenu = showMenu && !normalUser && !channel?.isReported && !item?.isReported
                  const itemOffset = Math.floor(index / CHANNEL_REELS_PAGE_SIZE) * CHANNEL_REELS_PAGE_SIZE;
                  const otherChannelParam = chId ? `${PATH_VIDEO_PREVIEW}/${chId}` : PATH_VIDEO_PREVIEW;
                  return (
                    <Card
                      key={item.maId}
                      item={cardItem}
                      url={isReel ? `${otherChannelParam}/${item.maId}?returnPath=${encodeURIComponent(location.pathname)}&offset=${itemOffset}` : '#'}
                      posterAspect={isReel ? '9:16' : '16:9'}
                      status={item.maStatus as 'APPROVED' | 'PENDING' | 'REJECTED' | 'approved' | 'pending' | 'rejected' | 'scheduled' | 'live'}
                      isReels={isReel}
                      isOverlayTestVisible={true}
                      onMenuClick={canShowMenu ? (e) => openMediaMenu(e, item) : undefined}
                      onClick={(e) => handleMediaClick(e, item)}
                      isChannel={true}
                    />
                  );
                })}
              </div>
            )}

            {channelVideos.length < channelVideosTotal && (
              <div className={styles.loadMoreContainer} style={{ textAlign: 'center', marginTop: '32px' }}>
                <Button
                  label={onLoading ? 'Loading...' : 'Load more'}
                  onClick={handleLoadMoreChannelVideos}
                  disabled={onLoading}
                  variant="outlined"
                />
              </div>
            )}
          </div>
        )}

        {activeTab === 'search' && (
          <div className={styles.section}>
            <ChannelSearchResults
              channelId={channel?.chId || ''}
              searchQuery={debouncedSearchQuery}
              onMediaMenuClick={openMediaMenu}
              currentLocation={`${location.pathname}?search=${searchQuery}&tab=search`}
              normalUser={normalUser}
              isReported={channel?.isReported}
            />
          </div>
        )}
      </div>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} role="dialog" size="small" shouldNotCloseButton>
        <ChannelAboutModal channel={channel} isReported={channel?.isReported} contactEmail={String(channel?.userInfo?.uEmailId || '')} videosCount={videosTotal} />
      </Dialog>

      <Popover
        isOpen={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        className={classNames(styles.popoverNoGap, menuAnchor?.isBottom && styles.popoverUp)}
        style={{
          position: 'fixed',
          top: menuAnchor?.y || 0,
          left: menuAnchor?.x || 0,
          transform: `translateX(-100%) ${menuAnchor?.isBottom ? 'translateY(-100%)' : ''}`,
          zIndex: 1000,
        }}
      >
        <div className={styles.popoverMenu}>
          {selectedIsReel && (
            <>
              <button className={styles.popoverItem} onClick={handleShowDetails}>
                <Icon icon={InfoIcon} className={styles.popoverIcon} />
                <span>Show details</span>
              </button>
              {hasPermission(user, PERMISSIONS.UPDATE_MEDIA) && <div className={styles.popoverDivider} />}
            </>
          )}
          {hasPermission(user, PERMISSIONS.UPDATE_MEDIA) && (
            <button className={styles.popoverItem} onClick={handleEditItem}>
              <Icon icon={EditIcon} className={styles.popoverIcon} />
              <span>Edit</span>
            </button>
          )}
          {hasPermission(user, PERMISSIONS.DELETE_MEDIA) && <div className={styles.popoverDivider} />}
          {hasPermission(user, PERMISSIONS.DELETE_MEDIA) && (
            <button className={classNames(styles.popoverItem, styles.deleteItem)} onClick={handleDeleteItem}>
              <Icon icon={DeleteIcon} className={styles.popoverIcon} />
              <span>Delete</span>
            </button>
          )}
        </div>
      </Popover>

      <ConfirmationDialog
        open={confirmationOpen}
        variant="delete"
        icon={InfoDelIcon}
        title={t('upload:delete-confirmation.title')}
        body={selectedIsReel ? t('upload:delete-confirmation.description') : t('upload:delete-confirmation.video_description')}
        confirmLabel={t('common:yes')}
        closeLabel={t('common:no')}
        onConfirm={() => {
          if (selectedItem) {
            deleteMediaApproval({ variables: { maId: selectedItem.maId } });
          }
        }}
        onClose={() => setConfirmationOpen(false)}
        busy={deleteLoading}
      />

      <ConfirmationDialog
        open={deleteChannelOpen}
        variant="delete"
        icon={InfoDelIcon}
        title={t('channel.delete.title')}
        body={t('channel.delete.body')}
        confirmLabel={t('channel.delete.confirm')}
        closeLabel={t('channel.delete.cancel')}
        onConfirm={() => {
          void handleRemoveChannel();
        }}
        onClose={() => setDeleteChannelOpen(false)}
        busy={removeChannelLoading}
      />

      <Dialog open={successOpen} onClose={() => setSuccessOpen(false)} role="alertdialog">
        <div className={styles.successDialog}>
          <div className={styles.successIconContainer}>
            <Icon icon={CheckIcon} />
          </div>
          <h2>{selectedIsReel ? t('upload:delete-success.title') : t('upload:delete-success-video.title')}</h2>
          <p>{selectedIsReel ? t('upload:delete-success.description') : t('upload:delete-success-video.description')}</p>
          <Button label={t('common:okay', { defaultValue: 'Okay' })} variant="contained" color="primary" onClick={() => setSuccessOpen(false)} fullWidth />
        </div>
      </Dialog>

      <Alert open={errorOpen} message={errorMessage} onClose={() => setErrorOpen(false)} />

      {reportModalOpen && (
        <ReportChannelModal
          onClose={() => setReportModalOpen(false)}
          onSuccess={() => {
            refetch()
          }}
        />
      )}
    </>
  );
};

export default ChannelPage;
